import { useState, useEffect } from 'react';
import { GraduationCap } from 'lucide-react';
import { Outlet } from 'react-router-dom';

const SPLINE_VIEWER_SRC = 'https://unpkg.com/@splinetool/viewer@1.10.53/build/spline-viewer.js';
const SPLINE_SCENE_URL = 'https://prod.spline.design/6Pzze82idzacQ8vy/scene.splinecode';
const DESKTOP_BREAKPOINT = 1024; // lg breakpoint

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT
  );

  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return isDesktop;
}

function useSplineLoader(shouldLoad: boolean) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!shouldLoad) return;

    // Already loaded from a previous mount
    if (customElements.get('spline-viewer')) {
      setReady(true);
      return;
    }

    const script = document.createElement('script');
    script.type = 'module';
    script.src = SPLINE_VIEWER_SRC;
    script.onload = () => setReady(true);
    document.body.appendChild(script);

    return () => {
      // Don't remove — cached for subsequent visits
    };
  }, [shouldLoad]);

  return ready;
}

function SplinePanel() {
  const isDesktop = useIsDesktop();
  const ready = useSplineLoader(isDesktop);

  if (!isDesktop) return null;

  return (
    <div className="hidden lg:flex lg:w-2/3 relative bg-gradient-to-br from-primary-900 via-primary-800 to-primary-600">
      {/* Loading skeleton */}
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-pulse text-primary-300 text-lg">Loading...</div>
        </div>
      )}

      {/* Spline 3D Animation — only rendered after script is loaded */}
      {ready && (
        <spline-viewer
          url={SPLINE_SCENE_URL}
          style={{ width: '100%', height: '100%' }}
          className="absolute inset-0 w-full h-full"
        />
      )}

      {/* Overlay content */}
      <div className="absolute inset-0 z-10 flex items-end justify-center bg-gradient-to-t from-primary-900/90 via-primary-900/20 to-transparent">
        <div className="w-full max-w-2xl px-12 pb-16 text-center space-y-6">
          <div className="flex items-center justify-center gap-3 text-white mb-8">
            <GraduationCap className="w-12 h-12" />
            <span className="text-3xl font-bold">Skillmine AI Tutor</span>
          </div>

          <h1 className="text-5xl font-bold text-white drop-shadow-2xl">
            Welcome to the Future of Learning
          </h1>

          <p className="text-primary-100 text-xl drop-shadow-lg max-w-xl mx-auto">
            Experience AI-powered education with personalized learning paths,
            interactive content, and real-time assistance.
          </p>

          <div className="text-primary-200 text-sm pt-8">
            © {new Date().getFullYear()} Skillmine. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AuthLayout() {
  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Spline Animation (2/3 width) */}
      <SplinePanel />

      {/* Right Panel - Auth Forms (1/3 width) */}
      <div className="w-full lg:w-1/3 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 text-center">
            <div className="flex items-center justify-center gap-3 text-primary-700 mb-6">
              <GraduationCap className="w-10 h-10" />
              <span className="text-2xl font-bold">Skillmine AI Tutor</span>
            </div>
          </div>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
